<template>
  <div>
    <v-flex xs10 offset-xs1 v-if="reportOfSickness">

      <div class='fl hf mb-5' style='align-items: flex-end;'>
        <div class='golden-left'>
          <h1 class='mb-3'>{{ 'report-of-sickness.detail' | translate }}</h1>
          <p>{{ 'report-of-sickness.form.detail-description' | translate }}</p>
        </div>

        <div class='golden-right'>
          <div v-if="!reportOfSickness.recovery" class='hf fl'>
            <new-period-card-button :reportOfSickness="reportOfSickness" @click="this.handleNewPeriodClick" class='mr-4' />
            <new-recovery-card-button :reportOfSickness="reportOfSickness" @click="this.handleRecoveryClick" />
          </div>
        </div>
      </div>

      <div class='fl hf'>
        <div class='golden-left' style='padding-top: 10px;'>
          <div v-for="(timelineElement, i) in timeline" :key="i + '-timeline-element-report-of-sickness'">
            <transition-group name="slide-y-reverse-transition">
              <recovery-timeline-element
                v-if="timelineElement.type === 'Recovery'"
                :reportOfSickness="reportOfSickness"
                :employee="employee"
                :prevDisabilityRate="timelineElement.prevDisabilityRate"
                :key="reportOfSickness.recovery.id + 'recovery-timeline-element'" />

              <period-timeline-element
                v-if="timelineElement.type === 'Period'"
                :reportOfSickness="reportOfSickness"
                :employee="employee"
                :period="timelineElement.period"
                :prevDisabilityRate="timelineElement.prevDisabilityRate"
                :key="timelineElement.period.id + '-period-timeline-element'" />

              <uwv-decision-timeline-element
                v-if="timelineElement.type === 'UwvDecision'"
                :reportOfSickness="reportOfSickness"
                :employee="employee"
                :uwvDecision="timelineElement.uwvDecision"
                :key="timelineElement.uwvDecision.id + '-uwv-decision'" />

              <report-of-sickness-timeline-element
                v-if="timelineElement.type === 'ReportOfSickness'"
                :reportOfSickness="reportOfSickness"
                :employee="employee"
                key='report-of-sickness-timeline-element' />

              <report-of-sickness-processed-date-element
                v-if="timelineElement.type === 'ProcessedDate' && i !== timeline.length - 1"
                :processedDate="timelineElement.date" key='rosidhe' />
            </transition-group>
          </div>
        </div>

        <div class='golden-right'>
          <report-of-sickness-details-employee :employee="employee" />
          <div>
            <new-uwv-decision-card-button
              :reportOfSickness="reportOfSickness"
              @click="handleNewUwvDecision"
              class='mt-5'
              v-if="isShowingNewUwvButton" />
          </div>
        </div>
      </div>
    </v-flex>
  </div>
</template>

<script>
import _ from 'lodash';

import Helpers from '@/shared/helpers/functions';

import Compositions from '@/views/helpers/compositions';
import ReportsOfSickness from '@/views/constants/reports-of-sickness';
import ReportsOfSicknessResource from '@/resources/constants/reports-of-sickness';
import UwvDecisions from '@/views/constants/uwv-decisions';

import NewPeriodCardButton from '@/views/components/generic/NewPeriodCardButton.vue';
import NewRecoveryCardButton from '@/views/components/generic/NewRecoveryCardButton.vue';
import NewUwvDecisionCardButton from '@/views/components/generic/NewUwvDecisionCardButton.vue';

import ReportOfSicknessDetailsEmployee from '@/views/components/report-of-sickness-details/ReportOfSicknessDetailsEmployee.vue';
import ReportOfSicknessProcessedDateElement from '@/views/components/report-of-sickness-details/ReportOfSicknessProcessedDateElement.vue';

import ReportOfSicknessTimelineElement from '@/views/components/report-of-sickness-details/ReportOfSicknessTimelineElement.vue';
import RecoveryTimelineElement from '@/views/components/report-of-sickness-details/RecoveryTimelineElement.vue';
import PeriodTimelineElement from '@/views/components/report-of-sickness-details/PeriodTimelineElement.vue';
import UwvDecisionTimelineElement from '@/views/components/report-of-sickness-details/UwvDecisionTimelineElement.vue';

export default {
  name: 'ReportOfSicknessDetails',
  components: {
    NewPeriodCardButton,
    NewRecoveryCardButton,
    NewUwvDecisionCardButton,

    ReportOfSicknessDetailsEmployee,

    ReportOfSicknessProcessedDateElement,
    ReportOfSicknessTimelineElement,
    RecoveryTimelineElement,
    PeriodTimelineElement,
    UwvDecisionTimelineElement
  },
  props: {
    _reportOfSickness: Object,
    _employee: Object
  },
  mounted () {
    this.$store.dispatch(ReportsOfSicknessResource.Actions.Load.START, this._reportOfSickness.id);
  },
  computed: {
    employee () {
      return this.$store.getters.employees
        .find(e => e.id === this._employee.id);
    },
    reportOfSickness () {
      const r = this.$store.getters.reportsOfSickness[this._employee.id]
        .find(r => r.id === this._reportOfSickness.id);
      return r;
    },

    timeline () {
      const result = [];
      let dr = this.reportOfSickness.disabilityRate;

      this.reportOfSickness.periods.forEach(p => {
        result.push({
          type: 'Period',
          date: p.startDate,
          prevDisabilityRate: dr,
          period: p
        });

        dr = p.disabilityRate;
      });

      if (this.reportOfSickness.recovery != null) {
        result.push({
          type: 'Recovery',
          date: this.reportOfSickness.recovery.recoveryDate,
          prevDisabilityRate: dr
        });
      }

      this.reportOfSickness.uwvDecisions.forEach(uwvDecision => {
        result.push({
          type: 'UwvDecision',
          date: uwvDecision.startDate,
          uwvDecision: uwvDecision
        });
      });

      result.push({
        type: 'ReportOfSickness',
        date: this.reportOfSickness.firstOfAbsence
      });

      let latestProcessedDate = Compositions.getLatestIndemnityProcessedDate(this.reportOfSickness);

      if (latestProcessedDate) {
        result.push({
          type: 'ProcessedDate',
          date: latestProcessedDate
        });
      }

      return result.sort((a, b) => {
        if (new Date(a.date) > new Date(b.date)) return -1;
        else if (new Date(b.date) > new Date(a.date)) return 1;
        else return 0;
      });
    },
    isShowingExtras () {
      const policies = Compositions.getPoliciesFromParticipations(
        this.employee.allParticipations, this.$store.getters.policies);

      if (policies && policies.length > 0) {
        return _.some(policies, p => p.productType === 'WIA' || p.categoryType === 'INZETBAAR');
      }
      return false;
    },
    isShowingNewUwvButton () {
      return this.reportOfSickness.recovery == null;
    }
  },
  methods: {
    formatDate: (date) => Helpers.Dates.formatDate(date),

    getPreviousDisabilityRateForRecovery () {
      if (this.reportOfSickness.periods.length > 0) {
        return this.reportOfSickness.periods[this.reportOfSickness.periods.length - 1].disabilityRate;
      } else return this.reportOfSickness.disabilityRate;
    },

    handleNewPeriodClick () {
      this.$store.dispatch(
        ReportsOfSickness.Actions.Tabs.OPEN_PERIOD, this.reportOfSickness);
    },
    handleRecoveryClick () {
      this.$store.dispatch(
        ReportsOfSickness.Actions.Tabs.OPEN_RECOVERY, this.reportOfSickness);
    },
    handleNewUwvDecision () {
      this.$store.dispatch(UwvDecisions.Actions.Tabs.New.OPEN, {
        reportOfSickness: this.reportOfSickness,
        employee: this.employee
      });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/stylesheets/variables.scss';

h2 {
  color: $primary;
}

h5 {
  font-size: 1.4em;
  letter-spacing: -.1px;
  font-family: 'Source Sans Pro';
}

h6 {
  position: relative;
  top: 1px;
}

.element-details {
  padding: 0px 15px;
}

.disability-rate-difference {
  position: relative;
  top: 2px;
  margin-right: 25px;
}

.confirmation-table {
  tr {
    &:first-child {
      td {
        border-top: 0;
        padding-top: 0;
      }
    }
    &:last-child {
      td {
        border-bottom: 0;
        padding-bottom: 0;
      }
    }
  }
}

.confirmation-table-adjust-padding {
  td {
    &:first-child {
      padding-left: 14px;
    }
    &:last-child {
      padding-right: 14px;
    }
  }
}

.report-detail {
  position: relative;
  top: -10px;
  margin-bottom: 30px;
}

.date-container {
  :nth-child(1) {
    font-weight: 600;
    color: $primary;
    font-size: 1.1em;
    margin-bottom: 10px;

    &.recovery { color: $positive; }
  }
}

.recovery {
  color: $positive;
}

.action-list {
  background-color: $secondary-lighten-3;
  padding: 30px;

  h3 {
    font-size: 1em;
    color: $secondary;
    margin-bottom: 20px;
  }

  .item {
    background-color: white;
    padding: 5px;

    p {
      color: $primary;
    }
  }
}

.status {
  color: $primary;

  &.to-review {
    padding: 6px 11px;
    background-color: #fff3c9;
    color: #de9400;
    display: inline-block;
    border-radius: $border-radius;
  }

  &.processed {
    padding: 6px 11px;
    background-color: #6fb14f;
    color: white;
    display: inline-block;
    font-weight: 600;
    border-radius: $border-radius;
  }
}
</style>
